import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ButtonData } from '../../../../../components/buttonGroup/ButtonGroup';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { DateFilterField } from '../../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import { useGetAllDiscountsQuery } from '../../../../../services/endpoints/orders/discounts';
import { useGetAllScholarshipsQuery } from '../../../../../services/endpoints/orders/scholarship';
import { useGetAllChildrenQuery } from '../../../../../services/endpoints/people/child';
import { useGetAllParentsQuery } from '../../../../../services/endpoints/people/parent';
import {
  Child,
  Scopes,
  useGetAdminsQuery,
  User
} from '../../../../../services/endpoints/people/user';
import { useGetCoursesQuery } from '../../../../../services/endpoints/schedule/course';
import { getCurrentUser } from '../../../../../services/helper';
import { Course } from '../../../../../types/course.type';
import { Discount, Scholarship } from '../../../../../types/orders.type';
import { scholarshipTypes } from '../../../../../utils/consts/orders/scholarships';
import { getFilterValue, parseDiscountsDeepLinkFilters } from '../../../../../utils/listFilterSearchParams';
import {Forbidden} from "../../../../../components/error/Forbidden";

const defaultScope = 'all';

const parentDropdownQuery = {
  scope: 'active_parents',
  data: { entries: 500000, orderBy: { field: 'first_name', order: 'asc' } }
};

const dropdownQuery = {
  data: { entries: 10000, orderBy: { field: 'first_name', order: 'asc' } }
};

// RBAC-FE-ORDERS: Discount admin - matches backend isAdmin middleware
// Global Admin IS included for Orders domain (unlike Finance)
export const userCanAdmin_Discounts = (user: User | null) =>
    !!(user?.isSuper ||
       user?.isSupremeAdmin ||
       user?.isAdmin ||
       user?.isGlobal ||
       user?.isLocationAdmin);

// Column definition needs access to currentUser for conditional rendering
// We'll pass it via a factory function or use the guard in the component
const createDiscountColumns = (currentUser: User | null): ColumnDef<Discount>[] => [
  {
    header: 'ID',
    accessorKey: 'id'
  },
  {
    header: 'Admin',
    accessorKey: 'admin_id',
    cell: ({ row }) => (
      <LinkCell
        content={`${row.original.user.first_name} ${row.original.user.last_name}`}
        url={`/admin/dash-users/admins/${row.original.admin_id}`}
      />
    )
  },
  {
    header: 'Parent',
    accessorKey: 'user_id',
    cell: ({ row }) => (
      <LinkCell
        content={`${row.original.user.first_name} ${row.original.user.last_name}`}
        url={`/admin/sfc-users/parents/${row.original.user_id}`}
      />
    )
  },
  {
    header: 'Scholarship',
    accessorKey: 'scholarship_id',
    cell: ({ row }) => (
      <>
        {scholarshipTypes[row.original.scholarship.scholarship_type]} (
        {row.original.scholarship.percent_discount}%&nbsp;off)
      </>
    )
  },
  {
    header: 'Session',
    accessorKey: 'course_id',
    cell: ({ row }) =>
      !row.original.course ? (
        ''
      ) : (
        <LinkCell
          content={row.original.course.title}
          url={`/admin/schedule/sessions/${row.original.course_id}`}
        />
      )
  },
  {
    header: 'Child',
    accessorKey: 'child_id',
    cell: ({ row }) =>
      !row.original.child ? (
        ''
      ) : (
        <LinkCell
          content={`${row.original.child.first_name} ${row.original.child.last_name}`}
          url={`/admin/users/children/${row.original.child_id}`}
        />
      )
  },
  {
    header: 'Order',
    accessorKey: 'order_id',
    cell: ({ row }) =>
      !row.original.order_id ? (
        ''
      ) : (
        <LinkCell
          content={`Order #${row.original.order_id}`}
          url={`/admin/orders/orders/${row.original.order_id}`}
        />
      )
  },
  {
    header: 'Gift Certificate',
    accessorKey: 'gift_certificate_id'
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        {userCanAdmin_Discounts(currentUser) && (
          <>
            <LinkCell
              content="View"
              url={`/admin/orders/discounts/${row.original.id}`}
              underline
            />
            <LinkCell
              content="Edit"
              url={`/admin/orders/discounts/${row.original.id}/edit`}
              underline
            />
          </>
        )}
      </Stack>
    )
  }
];

export const DiscountsView = () => {
  const currentUser = getCurrentUser();
  const { addToast } = useToast();
  const [filters, setFilters] = useListFiltersInUrl(
    { scope: defaultScope },
    { parseExtra: parseDiscountsDeepLinkFilters }
  );
  const [scopes, setScopes] = useState<Scopes>({});
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const canAccessDiscounts = userCanAdmin_Discounts(currentUser);

  const { data, error, isLoading, isFetching } = useGetAllDiscountsQuery(filters, {
    refetchOnMountOrArgChange: true,
    skip: !canAccessDiscounts
  });
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const navigate = useNavigate();
  const { data: coursesData, error: coursesError } = useGetCoursesQuery(
    { data: { entries: 10000, orderBy: { field: 'title', order: 'asc' } } },
    { skip: !canAccessDiscounts }
  );
  const { data: parentData, error: parentError } = useGetAllParentsQuery(parentDropdownQuery, {
    skip: !canAccessDiscounts
  });
  const { data: childrenData, error: childrenError } = useGetAllChildrenQuery(dropdownQuery, {
    skip: !canAccessDiscounts
  });
  const { data: adminData, error: adminError } = useGetAdminsQuery(dropdownQuery, {
    skip: !canAccessDiscounts
  });
  const { data: scholarshipData, error: scholarshipError } = useGetAllScholarshipsQuery(
    { data: { entries: 1000 } },
    { skip: !canAccessDiscounts }
  );

  // Create columns with currentUser context
  const discountColumns = createDiscountColumns(currentUser);

  const filterFormFields: FilterField[] = [
    {
      name: 'course_id',
      type: 'number',
      element: (
        <FormSelect
          name="course_id"
          label="Session"
          options={
            coursesData?.results?.map((course: Course) => ({
              value: course.id,
              label: course.title
            })) ?? []
          }
          loadingError={coursesError ? 'Error loading sessions' : undefined}
          selected_Value={coursesData?.results
            ?.map((course: Course) => ({ value: course.id, label: course.title }))
            .find(
              (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'course_id')
            )}
        />
      )
    },
    {
      name: 'user_id',
      type: 'number',
      element: (
        <FormSelect
          name="user_id"
          label="Parent"
          options={
            parentData?.results?.map((parent: User) => ({
              value: parent.id,
              label: `${parent.first_name} ${parent.last_name}`
            })) ?? []
          }
          loadingError={parentError ? 'Error loading parents' : undefined}
          selected_Value={parentData?.results
            ?.map((parent: User) => ({
              value: parent.id,
              label: `${parent.first_name} ${parent.last_name}`
            }))
            .find(
              (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'user_id')
            )}
        />
      )
    },
    {
      name: 'child_id',
      type: 'number',
      element: (
        <FormSelect
          name="child_id"
          label="Child"
          options={
            childrenData?.results?.map((child: Child) => ({
              value: child.id!,
              label: `${child.first_name} ${child.last_name}`
            })) ?? []
          }
          loadingError={childrenError ? 'Error loading children' : undefined}
          selected_Value={childrenData?.results
            ?.map((child: Child) => ({ value: child.id!, label: `${child.first_name} ${child.last_name}` }))
            .find(
              (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'child_id')
            )}
        />
      )
    },
    {
      name: 'admin_id',
      type: 'number',
      element: (
        <FormSelect
          name="admin_id"
          label="Admin"
          options={
            adminData?.results?.map((admin: User) => ({
              value: admin.id,
              label: `${admin.first_name} ${admin.last_name}`
            })) ?? []
          }
          loadingError={adminError ? 'Error loading admins' : undefined}
          selected_Value={adminData?.results
            ?.map((admin: User) => ({ value: admin.id, label: `${admin.first_name} ${admin.last_name}` }))
            .find(
              (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'admin_id')
            )}
        />
      )
    },
    {
      name: 'order_id',
      type: 'number',
      label: 'Order Number',
      value: getFilterValue(filters.data?.filterBy, 'order_id')
    },
    {
      name: 'gift_certificate_id',
      type: 'number',
      label: 'Gift Certificate',
      value: getFilterValue(filters.data?.filterBy, 'gift_certificate_id')
    },
    {
      name: 'scholarship_id',
      type: 'number',
      element: (
        <FormSelect
          name="scholarship_id"
          label="Scholarship"
          options={
            scholarshipData?.results?.map((item: Scholarship) => ({
              value: item.id,
              label: `${scholarshipTypes[item.scholarship_type]} (${item.percent_discount}% off)`
            })) ?? []
          }
          loadingError={scholarshipError ? 'Error loading scholarships' : undefined}
          selected_Value={scholarshipData?.results
            ?.map((item: Scholarship) => ({
              value: item.id,
              label: `${scholarshipTypes[item.scholarship_type]} (${item.percent_discount}% off)`
            }))
            .find(
              (option) =>
                String(option.value) === getFilterValue(filters.data?.filterBy, 'scholarship_id')
            )}
        />
      )
    },
    {
      name: 'created_at_from',
      id: 'created_at',
      operator: 'greater_equal_than',
      element: (
        <DateFilterField
          name="created_at_from"
          label="Created At"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'created_at', 'greater_equal_than')
    },
    {
      name: 'created_at_to',
      id: 'created_at',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="created_at_to" />,
      value: getFilterValue(filters.data?.filterBy, 'created_at', 'lesser_equal_than')
    }
  ];

  useEffect(() => {
    if (!isLoading && error) {
      addToast('Error while loading discounts', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isLoading]);

  useEffect(() => {
    if (!error && !isLoading && data && data.results) {
      if (data.scopes) {
        setScopes(
          data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
          data.scopes.map((sMap) =>
            Object.entries(sMap).map(([scope, title]) => {
              let scope_count = 0;
              if (data.scope_counts != null) {
                scope_count = data.scope_counts[scope] ?? 0;
              }
              return {
                title: `${title} (${scope_count})`,
                key: scope
              };
            })
          )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setDiscounts(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  // RBAC-FE-ORDERS: Defense-in-depth guard
  if (!canAccessDiscounts) {
    return <Forbidden />;
  }

  return (
    <div className="py-2">
      <SFCTable
        name={`${scopes[filters?.scope!] ?? ''} Discounts`}
        columns={discountColumns}
        data={discounts}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        scopes={buttons}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={filters.data?.filterBy}
        indexDownloadPath="discounts"
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={setFilters}
        newItemEl={
          userCanAdmin_Discounts(currentUser) && (
            <SimpleButton
              variant="sfc-blueberry"
              height={40}
              truncate={false}
              onClick={() => navigate('/admin/orders/discounts/new')}
            >
              New discount
            </SimpleButton>
          )
        }
      />
    </div>
  );
};
